import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ORG_LOGOS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  FileText,
  FileStack,
  Award,
  Users,
  ScrollText,
  CreditCard,
  LogOut,
  ChevronDown,
  Upload,
  Building2,
  Calendar,
  RefreshCw,
  GraduationCap,
  Gavel,
  Scale,
  Mail,
  FileImage,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SIDEBAR = {
  applicant: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/dashboard/drafts", icon: FileText, label: "Saved Drafts" },
    { section: "ACIS · Institutional Accreditation" },
    { to: "/dashboard/schools", icon: FileStack, label: "Accreditation Applications" },
    { to: "/dashboard/schools/new", icon: FileText, label: "New Accreditation Application" },
    { to: "/dashboard/accredited-schools", icon: Scale, label: "Accreditation Status" },
    { to: "/dashboard/site-visits", icon: Calendar, label: "Site Visits" },
    { to: "/dashboard/documents", icon: Upload, label: "Documents" },
    { section: "IBTHM · Board Certification (Practitioners)" },
    { to: "/dashboard/applications", icon: FileStack, label: "Certification Applications" },
    { to: "/dashboard/applications/new", icon: FileText, label: "New Certification" },
  ],
  practitioner: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true },
    { section: "Credential" },
    { to: "/dashboard/credentials", icon: Award, label: "My Credentials" },
    { to: "/dashboard/renewals", icon: RefreshCw, label: "Renewals" },
    { to: "/dashboard/ce", icon: GraduationCap, label: "CE Records" },
    { to: "/dashboard/reinstatement", icon: ScrollText, label: "Reinstatement" },
    { section: "Applications" },
    { to: "/dashboard/applications", icon: FileStack, label: "Applications" },
    { to: "/dashboard/applications/new", icon: FileText, label: "New Application" },
    { to: "/dashboard/documents", icon: Upload, label: "Documents" },
    { section: "ACIS" },
    { to: "/dashboard/schools", icon: Building2, label: "School Accreditation" },
  ],
  reviewer: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true },
    { section: "IBTHM Review" },
    { to: "/dashboard/applications", icon: FileStack, label: "Review Queue" },
    { to: "/dashboard/credentials", icon: Award, label: "Credentials" },
    { to: "/dashboard/ce", icon: GraduationCap, label: "CE Records" },
    { section: "ACIS Review" },
    { to: "/dashboard/schools", icon: Building2, label: "Accreditation Queue" },
    { to: "/dashboard/accredited-schools", icon: Scale, label: "Approved Schools" },
    { to: "/dashboard/site-visits", icon: Calendar, label: "Site Visits" },
  ],
  site_visitor: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true },
    { section: "ACIS" },
    { to: "/dashboard/site-visits", icon: Calendar, label: "My Site Visits" },
    { to: "/dashboard/schools", icon: Building2, label: "School Applications" },
  ],
  admin: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true },
    { section: "IBTHM" },
    { to: "/dashboard/applications", icon: FileStack, label: "Applications" },
    { to: "/dashboard/credentials", icon: Award, label: "Credentials" },
    { to: "/dashboard/renewals", icon: RefreshCw, label: "Renewals" },
    { to: "/dashboard/ce", icon: GraduationCap, label: "CE Records" },
    { to: "/dashboard/disciplinary", icon: Gavel, label: "Disciplinary" },
    { to: "/dashboard/reinstatement", icon: ScrollText, label: "Reinstatement" },
    { section: "ACIS" },
    { to: "/dashboard/schools", icon: Building2, label: "School Applications" },
    { to: "/dashboard/accredited-schools", icon: Scale, label: "Approved Schools" },
    { to: "/dashboard/site-visits", icon: Calendar, label: "Site Visits" },
    { section: "Administration" },
    { to: "/dashboard/payments", icon: CreditCard, label: "Payments" },
    { to: "/dashboard/emails", icon: Mail, label: "Email Deliveries" },
    { to: "/dashboard/seals", icon: FileImage, label: "Seal Management" },
    { to: "/dashboard/users", icon: Users, label: "Users" },
    { to: "/dashboard/audit", icon: ScrollText, label: "Audit Logs" },
    { to: "/dashboard/admin/trash", icon: Trash2, label: "Deleted Records" },
  ],
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const items = SIDEBAR[user.role] || SIDEBAR.applicant;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header
        className="sticky top-0 z-40 bg-white border-b border-slate-200 h-16 flex items-center px-4 sm:px-6"
        data-testid="dashboard-header"
      >
        <Link to="/" className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <img src={ORG_LOGOS.ACIS} alt="ACIS" className="h-10 w-10 object-contain" />
            <img src={ORG_LOGOS.IBTHM} alt="IBTHM" className="h-10 w-10 object-contain" />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-heading font-bold text-primary text-base tracking-tight">ACIS | IBTHM</div>
          </div>
        </Link>
        <div className="ml-auto flex items-center gap-4">
          <div className="hidden md:block text-right leading-tight">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Signed in as</div>
            <div className="text-sm font-semibold text-primary capitalize">{user.role}</div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="user-menu-btn" className="gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white text-xs font-bold">
                  {(user.name || user.email).slice(0, 2).toUpperCase()}
                </span>
                <span className="hidden sm:inline text-sm max-w-[160px] truncate">{user.name || user.email}</span>
                <ChevronDown className="h-4 w-4 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/")}>
                <FileText className="h-4 w-4 mr-2" /> Public Site
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-red-600 focus:text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex">
        <aside
          className="hidden md:flex flex-col w-60 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-4rem)] sticky top-16"
          data-testid="dashboard-sidebar"
        >
          <nav className="flex-1 py-6 px-3">
            <ul className="space-y-1">
              {items.map((item, idx) => {
                if (item.section) {
                  return (
                    <li key={`sec-${idx}`} className="pt-4 pb-1 px-3">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{item.section}</div>
                    </li>
                  );
                }
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-primary text-white shadow-sm"
                            : "text-slate-700 hover:bg-slate-100"
                        }`
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div className="px-3 pb-6">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 leading-relaxed">
              <div className="font-semibold text-primary mb-1">Need help?</div>
              Contact the credentialing office: <a className="underline" href="mailto:join@aciscommission.com">join@aciscommission.com</a>
            </div>
          </div>
        </aside>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full">{children}</main>
      </div>
    </div>
  );
}
